import { Message, scannerEmitter, Toast } from '@byecode/ui'
import type { TextConfig, TextValue } from '@lighthouse/core'
import { isWechatBrowserOrMiniProgram } from '@lighthouse/tools'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import wx from 'weixin-js-sdk'

import { useApplicationContext } from '../../../contexts'
import { useAppThemeContext } from '../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../FillPicker'
import { TagIcon } from '../../TagIcon'
import BaseInput from '../BaseInput'
import { inputHeightMap } from '../constant'
import { useFieldInputFont } from '../hooks/useFieldInputFont'
import type { FieldBaseProps } from '../types'
import { measureFontLineHeight } from '../utils'

interface TextFieldProps extends FieldBaseProps {
    // field: Field
    textConfig: TextConfig
    isMobile?: boolean
    value: TextValue
}
const size = 'middle'

const SCxContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: none !important;
`
const TextField: React.FunctionComponent<TextFieldProps> = props => {
    const { onBlur, onChange, value, defaultValue, isMobile, textConfig, readOnly, onKeyDown, onFocus } = props
    const { theme } = useAppThemeContext()
    const { placeholder, text: textConfigure, inputHeight = 'small', breakPoint, autofocus } = textConfig ?? {}
    const { disableInputs = [], scannerList = [], enableWxScanner } = textConfigure ?? {}
    // const [text, setText] = useState(value)
    const [isFocus, setIsFocus] = useState(false)
    const { isBuilder } = useApplicationContext()
    const scannerReadOnly = disableInputs.includes('album')
    const inputReadOnly = readOnly || disableInputs.includes('input')
    const placeholderColor = applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes)
    const { customFontSize, customFontFamily, customFontWeight } = useFieldInputFont(breakPoint)

    const handleChange = useCallback(
        (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
            const outputValue = ev.target.value
            // setText(outputValue)
            onChange?.({ type: 'text', value: outputValue })
        },
        [onChange]
    )

    const handleFocus = useCallback(() => {
        setIsFocus(true)
        onFocus?.()
    }, [onFocus])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLElement>) => {
            onKeyDown?.(e)
        },
        [onKeyDown]
    )

    const handleBlur = useCallback(() => {
        setIsFocus(false)
        onBlur?.({ value, type: 'text' })
    }, [onBlur, value])

    const handleScanner = useCallback(() => {
        if (readOnly) {
            return
        }
        if (isBuilder) {
            Toast.warning('请前往移动端应用界面中扫码')
            return
        }
        const isWechat = isWechatBrowserOrMiniProgram()
        if (isWechat && enableWxScanner) {
            const isOpenDebug = window.location.href.includes('byecode-debug')
            wx.ready(() => {
                wx.checkJsApi({
                    jsApiList: ['scanQRCode'],
                    // 需要检测的JS接口列表，所有JS接口列表见附录2,
                    success(info) {
                        isOpenDebug && Message.success({ title: `checkJsApi: ${JSON.stringify(info)}` })
                        wx.scanQRCode({
                            needResult: 1, // 默认为0，扫描结果由微信处理，1则直接返回扫描结果，
                            scanType: scannerList, // 可以指定扫二维码还是一维码，默认二者都有
                            success(res) {
                                const outputValue = res.resultStr
                                if (outputValue) {
                                    onChange?.({ type: 'text', value: outputValue })
                                }
                            },
                            fail(err) {
                                isOpenDebug && Toast.error(`scanQRCode: ${err?.errMsg}`)
                            }
                        })
                    },
                    fail(err) {
                        isOpenDebug && Toast.error(`checkJsApi: ${err?.errMsg}`)
                    }
                })
            })

            return
        }
        scannerEmitter.emit('openScanner', {
            option: {
                scanType: scannerList,
                enableUpload: !scannerReadOnly
            },
            success: v => {
                // setText(v)
                onChange?.({ value: v, type: 'text' })
            }
        })
    }, [readOnly, isBuilder, enableWxScanner, scannerList, scannerReadOnly, onChange])

    return (
        <SCxContainer>
            <BaseInput
                value={value}
                size={size}
                data-field-input
                data-field-active={isFocus}
                data-field-disabled={!!inputReadOnly}
                inputProps={{
                    autoFocus: autofocus,
                    placeholder,
                    onFocus: handleFocus,
                    onBlur: handleBlur,
                    onChange: handleChange,
                    onKeyDown: handleKeyDown
                }}
                readOnly={inputReadOnly}
                // style={{
                //     minHeight: fontLineHeight != null
                //         ? Math.max(inputHeightMap[inputHeight], fontLineHeight)
                //         : inputHeightMap[inputHeight]
                // }}
                minHeight={inputHeightMap[inputHeight]}
                placeholderColor={placeholderColor}
                fontSize={customFontSize}
                fontFamily={customFontFamily}
                fontWeight={customFontWeight}
            />
            {scannerList?.length > 0 && isMobile && (
                <TagIcon
                    icon="Scanner"
                    disabled={scannerReadOnly}
                    size={40}
                    data-field-extra
                    data-field-disabled={!!readOnly}
                    radius={7}
                    onClick={handleScanner}
                    style={{ flexShrink: 0 }}
                />
            )}
        </SCxContainer>
    )
}

export default TextField
